import { useCallback, useEffect, useState } from 'react';
import { prefersReducedMotion, ROW_EXIT_MS } from './rowExit';

function withId(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
    return new Set([...ids, id]);
}

function withoutId(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
    return new Set([...ids].filter((existing) => existing !== id));
}

// Handled items animate out ("leaving"), then stay hidden until a refetch confirms they're gone.
export function useLeavingIds(presentIds: readonly string[]) {
    const [leavingIds, setLeavingIds] = useState<ReadonlySet<string>>(() => new Set());
    const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(() => new Set());

    const hide = useCallback((id: string) => {
        setLeavingIds((current) => withoutId(current, id));
        setHiddenIds((current) => withId(current, id));
    }, []);

    // Resolves once the item is out of view, so the caller can hold its refetch until then.
    // No timer cleanup: the mutation awaits this even if the page unmounts meanwhile.
    const markLeaving = useCallback(
        (id: string): Promise<void> => {
            if (prefersReducedMotion()) {
                hide(id);
                return Promise.resolve();
            }
            setLeavingIds((current) => withId(current, id));
            return new Promise((resolve) => {
                setTimeout(() => {
                    hide(id);
                    resolve();
                }, ROW_EXIT_MS);
            });
        },
        [hide]
    );

    // Forget ids the data no longer has, so an item that comes back later can show again.
    useEffect(() => {
        if (hiddenIds.size === 0) return;
        const present = new Set(presentIds);
        const stillPresent = [...hiddenIds].filter((id) => present.has(id));
        if (stillPresent.length !== hiddenIds.size) setHiddenIds(new Set(stillPresent));
    }, [presentIds, hiddenIds]);

    return { leavingIds, hiddenIds, markLeaving };
}
